import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { backupService } from '../services/api';
import { ArrowPathIcon, BoltIcon } from '@heroicons/react/24/outline';
import { BackupSlotReport, SlotReportItem, TimeSlot } from '../types';

// Type pour le résultat de la synchronisation
interface SyncResult {
  success: boolean;
  totalWebsites: number;
  synchronized: number;
  alreadyInSync: number;
  skipped: number;
  errors: number;
  message: string;
}

const BackupSlotsPage: React.FC = () => {
  const [report, setReport] = useState<BackupSlotReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [synchronizing, setSynchronizing] = useState<boolean>(false);
  const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

  // Charger le rapport des tranches horaires
  useEffect(() => {
    fetchReport();
  }, []);

  // Fonction pour récupérer le rapport
  const fetchReport = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await backupService.getBackupSlotsReport();
      setReport(data.report);
    } catch (error: any) {
      console.error('Erreur lors du chargement du rapport:', error);
      setError(error.response?.data?.message || 'Impossible de charger le rapport. Veuillez réessayer plus tard.');
    } finally {
      setLoading(false);
    }
  };

  // Obtenir le pourcentage d'utilisation des tranches
  const getUsagePercentage = () => {
    if (!report) return 0;
    // Calculer le pourcentage de tranches utilisées
    return Math.round((report.usedSlots / report.totalSlots) * 100);
  };

  // Fonction pour rafraîchir le rapport
  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchReport();
    setRefreshing(false);
  };

  // Fonction pour synchroniser les sites avec les tranches
  const handleSync = async () => {
    try {
      setSynchronizing(true);
      setSyncResult(null);
      
      const result = await backupService.syncWebsitesWithBackupSlots();
      setSyncResult(result);
      
      // Actualiser le rapport après la synchronisation
      await fetchReport();
    } catch (error: any) {
      console.error('Erreur lors de la synchronisation:', error);
      setError(error.response?.data?.message || 'Erreur lors de la synchronisation.');
    } finally {
      setSynchronizing(false);
    }
  };

  // Ajouter une fonction pour obtenir le décalage en minutes pour une tranche
  const getOffsetForSlot = (slotNumber: number): number => {
    const offsetMap: {[key: number]: number} = {
      1: 5,   2: 10,  3: 15,  4: 20,  5: 25,  6: 30,  7: 35,
      8: 40,  9: 45,  10: 50, 11: 55, 12: 0,  13: 2,  14: 7,  
      15: 12, 16: 17, 17: 22, 18: 27, 19: 32, 20: 37, 21: 42, 
      22: 47, 23: 52, 24: 57, 25: 1,  26: 3,  27: 11, 28: 13
    };
    
    return offsetMap[slotNumber] || 0;
  };

  // Afficher un loader pendant le chargement
  if (loading && !report) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  // Afficher une erreur si nécessaire
  if (error && !report) {
    return (
      <div className="bg-red-100 dark:bg-red-900 p-4 rounded-md text-red-800 dark:text-red-200">
        <p>{error}</p>
        <button 
          onClick={fetchReport} 
          className="mt-4 btn-primary"
        >
          Réessayer
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Tranches horaires de backup</h1>
          <p className="text-gray-500 dark:text-gray-400">
            Répartition des sites sur les créneaux de backup
          </p>
        </div>
        
        <div className="flex space-x-4">
          <button 
            onClick={handleSync}
            disabled={synchronizing}
            className="btn-secondary flex items-center"
          >
            {synchronizing ? (
              <>
                <BoltIcon className="animate-pulse h-5 w-5 mr-2" />
                Synchronisation...
              </>
            ) : (
              <>
                <BoltIcon className="h-5 w-5 mr-2" />
                Synchroniser
              </>
            )}
          </button>
          
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-primary flex items-center"
          >
            {refreshing ? (
              <>
                <ArrowPathIcon className="animate-spin h-5 w-5 mr-2" />
                Rafraîchissement...
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Rafraîchir
              </>
            )}
          </button>
        </div>
      </div>

      {/* Afficher les résultats de synchronisation */}
      {syncResult && (
        <div className="mb-6 bg-green-50 dark:bg-green-900 p-4 rounded-md text-green-800 dark:text-green-200">
          <h2 className="text-lg font-semibold mb-2">Résultat de la synchronisation</h2>
          <p>{syncResult.message}</p>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-3">
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Total sites</span>
              <p className="font-semibold">{syncResult.totalWebsites}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Synchronisés</span>
              <p className="font-semibold">{syncResult.synchronized}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Déjà synchronisés</span>
              <p className="font-semibold">{syncResult.alreadyInSync}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Ignorés</span>
              <p className="font-semibold">{syncResult.skipped}</p>
            </div>
            <div>
              <span className="text-sm text-gray-600 dark:text-gray-400">Erreurs</span>
              <p className="font-semibold">{syncResult.errors}</p>
            </div>
          </div>
        </div>
      )}
      
      {report && (
        <>
          {/* Statistiques générales */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-2">Tranches totales</h2>
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{report.totalSlots}</p>
            </div>
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-2">Sites assignés</h2>
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{report.totalSites}</p>
            </div>
            <div className="card p-6">
              <h2 className="text-xl font-semibold mb-2">Tranches disponibles</h2>
              <p className="text-3xl font-bold text-primary-600 dark:text-primary-400">{report.totalSlots - report.usedSlots}</p>
              <div className="mt-2 h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-primary-500"
                  style={{ width: `${getUsagePercentage()}%` }}
                ></div>
              </div>
              <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">
                {getUsagePercentage()}% des tranches utilisées
              </p>
            </div>
          </div>
          
          {/* Tableau des tranches */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Tranche
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sites
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Horaires disponibles
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Sites assignés
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-800">
                  {report.usageBySlot.sort((a: SlotReportItem, b: SlotReportItem) => a.slot - b.slot).map((slotInfo: SlotReportItem) => (
                    <tr key={`slot-${slotInfo.slot}`} className={slotInfo.websiteCount > 0 ? 'bg-gray-50 dark:bg-gray-800' : ''}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                        <div>Tranche {slotInfo.slot}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          (+{getOffsetForSlot(slotInfo.slot)}min)
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {slotInfo.websiteCount > 0 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800 dark:bg-primary-900 dark:text-primary-300">
                            {slotInfo.websiteCount}
                          </span>
                        ) : (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                            Libre
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {/* Vérifier si la tranche a des horaires disponibles */}
                        <div>
                          <p className="text-xs font-medium mb-1">Horaires disponibles:</p>
                          <div className="flex flex-wrap gap-1">
                            {/* Générer les horaires disponibles basés sur le décalage */}
                            {[21, 22, 23, 0, 1, 2, 3, 4, 5, 6].map((hour, index) => {
                              const minuteOffset = getOffsetForSlot(slotInfo.slot);
                              const formattedHour = hour.toString().padStart(2, '0');
                              const formattedMinute = minuteOffset.toString().padStart(2, '0');
                              const timeString = `${formattedHour}:${formattedMinute}`;
                              
                              // Vérifier si cet horaire est déjà utilisé
                              const isUsed = slotInfo.websites.some(
                                website => website.specificTime === timeString
                              );
                              
                              // N'afficher que les horaires non utilisés
                              return !isUsed ? (
                                <span 
                                  key={index} 
                                  className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
                                >
                                  {timeString}
                                </span>
                              ) : null;
                            }).filter(Boolean)}
                          </div>
                          {slotInfo.websites.length >= 10 && (
                            <span className="text-gray-500 dark:text-gray-400 text-sm mt-1">
                              Aucun horaire disponible
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {slotInfo.websites.length > 0 ? (
                          <ul className="space-y-1">
                            {slotInfo.websites.map((website: { id: string, name: string, url: string, specificTime?: string }) => (
                              <li key={website.id} className="flex items-start space-x-1">
                                <span className="text-gray-800 dark:text-gray-200 font-medium">
                                  {website.name || 'Sans nom'}
                                </span>
                                <span>-</span>
                                <a 
                                  href={website.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer" 
                                  className="text-blue-600 dark:text-blue-400 hover:underline truncate max-w-xs"
                                >
                                  {website.url}
                                </a>
                                {website.specificTime && (
                                  <span className="text-gray-500 dark:text-gray-400">
                                    {website.specificTime}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="text-green-600 dark:text-green-400">Disponible</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BackupSlotsPage; 